import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import { toast } from "sonner";

export type NotificationCategory = "social" | "challenge" | "finance" | "system";
export type NotificationKind =
  | "friend_request_received"
  | "friend_request_accepted"
  | "friend_request_declined"
  | "challenge_received"
  | "challenge_accepted"
  | "challenge_declined"
  | "challenge_completed"
  | "challenge_result_ready"
  | "challenge_expired"
  | "rematch_received"
  | "payment_due_3d"
  | "payment_due_1d"
  | "payment_due_today"
  | "payment_overdue"
  | "system_info";

export interface AppNotification {
  id: string;
  recipient_id: string;
  actor_id: string | null;
  kind: NotificationKind;
  category: NotificationCategory;
  payload: Record<string, unknown>;
  deep_link: string | null;
  read_at: string | null;
  created_at: string;
}

const HIGH_PRIORITY: NotificationKind[] = [
  "challenge_received",
  "challenge_accepted",
  "challenge_result_ready",
  "payment_due_today",
  "payment_overdue",
  "friend_request_received",
];

const TITLES: Record<NotificationKind, string> = {
  friend_request_received: "New friend request",
  friend_request_accepted: "Friend request accepted",
  friend_request_declined: "Friend request declined",
  challenge_received: "You got challenged",
  challenge_accepted: "Your challenge was accepted",
  challenge_declined: "Challenge declined",
  challenge_completed: "Challenge completed",
  challenge_result_ready: "Challenge results are in",
  challenge_expired: "Challenge expired",
  rematch_received: "Rematch request",
  payment_due_3d: "Credit card payment due in 3 days",
  payment_due_1d: "Credit card payment due tomorrow",
  payment_due_today: "Credit card payment due today",
  payment_overdue: "Credit card payment overdue",
  system_info: "Notification",
};

export function notificationTitle(n: AppNotification): string {
  return (n.payload?.title as string) || TITLES[n.kind] || "Notification";
}

export function useNotifications() {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [loading, setLoading] = useState(true);
  const seenIdsRef = useRef<Set<string>>(new Set());

  const refresh = useCallback(async () => {
    if (!user) {
      setNotifications([]);
      setLoading(false);
      return;
    }
    const { data } = await supabase
      .from("social_notifications")
      .select("*")
      .eq("recipient_id", user.id)
      .order("created_at", { ascending: false })
      .limit(50);
    const items = ((data) || []) as AppNotification[];
    items.forEach((n) => seenIdsRef.current.add(n.id));
    setNotifications(items);
    setLoading(false);
  }, [user]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Realtime subscription with toast for high-priority kinds
  useEffect(() => {
    if (!user) return;
    const channelName = `notifications_${user.id}_${Math.random().toString(36).slice(2, 8)}`;
    const channel = supabase
      .channel(channelName)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "social_notifications",
          filter: `recipient_id=eq.${user.id}`,
        },
        (payload) => {
          const n = payload.new as AppNotification;
          if (seenIdsRef.current.has(n.id)) return;
          seenIdsRef.current.add(n.id);
          setNotifications((prev) => [n, ...prev].slice(0, 50));

          if (HIGH_PRIORITY.includes(n.kind)) {
            const title = notificationTitle(n);
            toast(title, {
              description: n.deep_link ? "Tap the bell to view" : undefined,
            });
          }
        }
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  const unreadCount = notifications.filter((n) => !n.read_at).length;

  const markAllRead = useCallback(async () => {
    if (!user || unreadCount === 0) return;
    const now = new Date().toISOString();
    setNotifications((prev) => prev.map((n) => ({ ...n, read_at: n.read_at ?? now })));
    await supabase
      .from("social_notifications")
      .update({ read_at: now })
      .eq("recipient_id", user.id)
      .is("read_at", null);
  }, [user, unreadCount]);

  const markRead = useCallback(
    async (id: string) => {
      if (!user) return;
      const now = new Date().toISOString();
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, read_at: n.read_at ?? now } : n))
      );
      await supabase
        .from("social_notifications")
        .update({ read_at: now })
        .eq("id", id)
        .eq("recipient_id", user.id);
    },
    [user]
  );

  return { notifications, unreadCount, loading, refresh, markAllRead, markRead };
}
